import React, { useState, useEffect } from 'react';
import { Code2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AdminJsonToggleProps {
  data: unknown;
  onUpdate?: (data: unknown) => void;
  label?: string;
}

/**
 * AdminJsonToggle provides a consistent JSON editing interface for debugging
 * across all module types. Shows a toggle button that reveals a JSON editor.
 */
const AdminJsonToggle: React.FC<AdminJsonToggleProps> = ({ 
  data, 
  onUpdate,
  label = 'Edit JSON'
}) => {
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Update JSON text when data changes
  useEffect(() => {
    setJsonText(JSON.stringify(data, null, 2));
    setError(null);
  }, [data]);

  // Handle JSON editing
  const handleJsonKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      try {
        const parsed = JSON.parse(jsonText);
        setError(null);
        if (onUpdate) {
          onUpdate(parsed);
        }
      } catch (error) {
        setError('Invalid JSON format');
        console.error('Invalid JSON:', error);
      }
    }
  };

  return (
    <>
      {/* JSON Toggle Button */}
      <div className="absolute top-20 right-4 z-10">
        <Button
          onClick={() => setShowJson(!showJson)}
          variant="outline"
          size="sm"
          className="rounded-full"
          title={label}
        >
          {showJson ? <X className="h-4 w-4" /> : <Code2 className="h-4 w-4" />}
        </Button>
      </div>
      
      {/* JSON Editor */}
      {showJson && (
        <div className="absolute top-32 right-4 left-4 z-10 max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4">
            <div className="mb-2 flex justify-between items-center">
              <h3 className="text-sm font-medium text-gray-700">{label}</h3>
              {error && (
                <span className="text-xs text-red-600">{error}</span>
              )}
            </div>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              onKeyDown={handleJsonKeyDown}
              className="w-full h-64 p-4 font-mono text-sm bg-gray-50 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Edit JSON and press Enter to apply changes"
            />
            <div className="mt-2 text-xs text-gray-500 text-right">
              Press Enter to apply {onUpdate ? 'changes' : '(read-only)'}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminJsonToggle;